(() => {
  const selector = "[data-carousel]";
  const carouselLabels = {"en":["Previous items","Next items","Select page","Go to page"],"pt":["Itens anteriores","Próximos itens","Selecionar página","Ir para a página"],"de":["Vorherige Artikel","Nächste Artikel","Seite auswählen","Zur Seite"],"es":["Artículos anteriores","Siguientes artículos","Seleccionar página","Ir a la página"],"fr":["Articles précédents","Articles suivants","Sélectionner une page","Aller à la page"],"it":["Articoli precedenti","Articoli successivi","Seleziona pagina","Vai alla pagina"]};
  const language = (document.documentElement.lang || "en").split("-")[0];
  const labels = carouselLabels[language] || carouselLabels.en;

  function addGlobalStyles() {
    if (document.getElementById("global-carousel-styles")) return;

    const style = document.createElement("style");
    style.id = "global-carousel-styles";

    style.textContent = `
        .global-carousel{position:relative;width:100%;min-width:0;max-width:100%;overflow:hidden}

        [data-carousel]{display:flex!important;width:100%;min-width:0;overflow-x:auto;overscroll-behavior-x:contain;scroll-behavior:smooth;scroll-snap-type:x mandatory;scrollbar-width:none;touch-action:auto;-webkit-overflow-scrolling:touch}

        [data-carousel][data-carousel-snap="proximity"]{scroll-snap-type:x proximity}

        [data-carousel]::-webkit-scrollbar{display:none}

        [data-carousel]>*{min-width:0;scroll-snap-align:start;scroll-snap-stop:normal}

        [data-carousel] img{-webkit-user-drag:none;user-select:none}

        .global-carousel__arrow{position:absolute;z-index:2;top:50%;width:36px;height:36px;border:1px solid #e2e2e2;border-radius:50%;background:#fff;color:#222;font-size:24px;line-height:1;cursor:pointer;box-shadow:0 3px 12px rgba(0,0,0,.12);transform:translateY(-50%)}

        .global-carousel__arrow--previous{left:6px}
        .global-carousel__arrow--next{right:6px}
        .global-carousel__arrow:disabled{opacity:.3;cursor:default}

        .global-carousel__dots{position:absolute;z-index:4;left:50%;bottom:10px;display:flex;align-items:center;justify-content:center;gap:7px;padding:6px 9px;border-radius:100px;background:rgba(255,255,255,.82);box-shadow:0 2px 8px rgba(0,0,0,.12);transform:translateX(-50%);backdrop-filter:blur(5px)}

        .global-carousel__dot{display:block;width:8px;height:8px;padding:0;border:0;border-radius:100px;background:#999;cursor:pointer;transition:width .25s ease,background-color .25s ease,transform .25s ease}

        .global-carousel__dot:hover{background:#555;transform:scale(1.15)}
        .global-carousel__dot--active{width:24px;background:#ff6200}
        .global-carousel__dot:focus-visible{outline:2px solid #111;outline-offset:2px}
        `;

    document.head.appendChild(style);
  }

  class GlobalCarousel {
    constructor(track) {
      if (track.carouselInstance) return;

      this.track = track;
      this.cards = [...track.children];
      this.autoplayTimer = null;
      this.dotButtons = [];
      this.currentPageCount = 0;

      this.options = {
        items: Number(track.dataset.carouselItems || 4),
        tablet: Number(track.dataset.carouselTablet || 2),
        mobile: Number(track.dataset.carouselMobile || 1.2),
        step: Number(track.dataset.carouselStep || 1),
        gap: Number(track.dataset.carouselGap || 8),
        arrows: track.dataset.carouselArrows !== "false",
        dots: track.dataset.carouselDots === "true",
        loop: track.dataset.carouselLoop === "true",
        autoplay: track.dataset.carouselAutoplay === "true",
        interval: Math.max(
          1000,
          Number(track.dataset.carouselInterval || 5000)
        ),
        pauseOnHover:
          track.dataset.carouselPauseHover !== "false"
      };

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      this.create();
      track.carouselInstance = this;
    }

    create() {
      this.shell = document.createElement("div");
      this.shell.className = "global-carousel";

      this.track.parentNode.insertBefore(
        this.shell,
        this.track
      );

      this.shell.appendChild(this.track);

      this.track.style.gap =
        `${this.options.gap}px`;

      this.track.tabIndex = 0;

      if (this.options.arrows) {
        this.createArrows();
      }

      if (this.options.dots) {
        this.createDotsContainer();
      }

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => {
          this.update();
        });

        this.resizeObserver.observe(this.track);
      } else {
        window.addEventListener("resize", () => {
          this.update();
        });
      }

      this.track.addEventListener(
        "scroll",
        () => {
          requestAnimationFrame(() => {
            this.updateArrows();
            this.updateDots();
          });
        },
        { passive: true }
      );

      this.track.addEventListener("keydown", event => {
        if (event.key === "ArrowRight") {
          event.preventDefault();
          this.next();
          this.restartAutoplay();
        }

        if (event.key === "ArrowLeft") {
          event.preventDefault();
          this.previous();
          this.restartAutoplay();
        }
      });

      this.track.addEventListener(
        "pointerdown",
        () => {
          this.stopAutoplay();
        },
        { passive: true }
      );

      this.track.addEventListener(
        "pointerup",
        () => {
          this.startAutoplay();
        },
        { passive: true }
      );

      this.track.addEventListener(
        "pointercancel",
        () => {
          this.startAutoplay();
        },
        { passive: true }
      );

      if (this.options.pauseOnHover) {
        this.shell.addEventListener(
          "mouseenter",
          () => {
            this.stopAutoplay();
          }
        );

        this.shell.addEventListener(
          "mouseleave",
          () => {
            this.startAutoplay();
          }
        );

        this.shell.addEventListener(
          "focusin",
          () => {
            this.stopAutoplay();
          }
        );

        this.shell.addEventListener(
          "focusout",
          event => {
            if (!this.shell.contains(event.relatedTarget)) {
              this.startAutoplay();
            }
          }
        );
      }

      this.visibilityHandler = () => {
        if (document.hidden) {
          this.stopAutoplay();
        } else {
          this.startAutoplay();
        }
      };

      document.addEventListener(
        "visibilitychange",
        this.visibilityHandler
      );

      this.update();
      this.startAutoplay();
    }

    createArrows() {
      this.previousButton = this.createButton(
        "‹",
        "global-carousel__arrow--previous",
        labels[0]
      );

      this.nextButton = this.createButton(
        "›",
        "global-carousel__arrow--next",
        labels[1]
      );

      this.previousButton.addEventListener(
        "click",
        () => {
          this.previous();
          this.restartAutoplay();
        }
      );

      this.nextButton.addEventListener(
        "click",
        () => {
          this.next();
          this.restartAutoplay();
        }
      );

      this.shell.append(
        this.previousButton,
        this.nextButton
      );
    }

    createButton(symbol, modifier, label) {
      const button =
        document.createElement("button");

      button.type = "button";
      button.className =
        `global-carousel__arrow ${modifier}`;

      button.innerHTML = symbol;
      button.setAttribute("aria-label", label);

      return button;
    }

    createDotsContainer() {
      this.dotsContainer =
        document.createElement("div");

      this.dotsContainer.className =
        "global-carousel__dots";

      this.dotsContainer.setAttribute(
        "role",
        "tablist"
      );

      this.dotsContainer.setAttribute(
        "aria-label",
        "Selecionar página"
      );

      this.shell.appendChild(
        this.dotsContainer
      );
    }

    renderDots() {
      if (!this.options.dots) return;

      const pageCount = this.getPageCount();

      if (pageCount === this.currentPageCount) {
        this.updateDots();
        return;
      }

      this.currentPageCount = pageCount;
      this.dotButtons = [];
      this.dotsContainer.innerHTML = "";

      for (let index = 0; index < pageCount; index++) {
        const dot = document.createElement("button");

        dot.type = "button";
        dot.className = "global-carousel__dot";
        dot.setAttribute("role", "tab");

        dot.setAttribute(
          "aria-label",
          `${labels[3]} ${index + 1}`
        );

        dot.addEventListener("click", () => {
          this.goToPage(index);
          this.restartAutoplay();
        });

        this.dotButtons.push(dot);
        this.dotsContainer.appendChild(dot);
      }

      this.dotsContainer.hidden =
        pageCount <= 1;

      this.updateDots();
    }

    getVisibleItems() {
      if (window.innerWidth <= 600) {
        return this.options.mobile;
      }

      if (window.innerWidth <= 900) {
        return this.options.tablet;
      }

      return this.options.items;
    }

    getMaximumScroll() {
      return Math.max(
        0,
        this.track.scrollWidth -
          this.track.clientWidth
      );
    }

    getPageDistance() {
      return (
        (this.cardWidth + this.options.gap) *
        this.options.step
      );
    }

    getPageCount() {
      const maximum = this.getMaximumScroll();
      const distance = this.getPageDistance();

      if (maximum <= 2 || distance <= 0) {
        return 1;
      }

      return Math.ceil(maximum / distance) + 1;
    }

    getCurrentPage() {
      const distance = this.getPageDistance();

      if (distance <= 0) return 0;

      const page = Math.round(
        this.track.scrollLeft / distance
      );

      return Math.max(
        0,
        Math.min(page, this.getPageCount() - 1)
      );
    }

    hasOverflow() {
      return this.getMaximumScroll() > 2;
    }

    isAtBeginning() {
      return this.track.scrollLeft <= 2;
    }

    isAtEnd() {
      return (
        this.track.scrollLeft >=
        this.getMaximumScroll() - 2
      );
    }

    goToPage(index) {
      const target = Math.min(
        index * this.getPageDistance(),
        this.getMaximumScroll()
      );

      this.track.scrollTo({
        left: target,
        behavior: "smooth"
      });
    }

    update() {
      const visibleItems =
        this.getVisibleItems();

      const gapSpace =
        this.options.gap *
        Math.max(0, visibleItems - 1);

      this.cardWidth =
        (this.track.clientWidth - gapSpace) /
        visibleItems;

      this.cards.forEach(card => {
        card.style.flex =
          `0 0 ${this.cardWidth}px`;
      });

      this.updateArrows();
      this.renderDots();

      if (this.options.autoplay) {
        this.restartAutoplay();
      }
    }

    next() {
      if (!this.hasOverflow()) return;

      if (this.isAtEnd()) {
        if (this.options.loop) {
          this.goToPage(0);
        } else {
          this.stopAutoplay();
        }

        return;
      }

      this.move(1);
    }

    previous() {
      if (!this.hasOverflow()) return;

      if (this.isAtBeginning()) {
        if (this.options.loop) {
          this.goToPage(
            this.getPageCount() - 1
          );
        }

        return;
      }

      this.move(-1);
    }

    move(direction) {
      const distance =
        this.getPageDistance() * direction;

      this.track.scrollBy({
        left: distance,
        behavior: "smooth"
      });
    }

    updateArrows() {
      if (!this.options.arrows) return;

      if (!this.hasOverflow()) {
        this.previousButton.disabled = true;
        this.nextButton.disabled = true;
        return;
      }

      if (this.options.loop) {
        this.previousButton.disabled = false;
        this.nextButton.disabled = false;
        return;
      }

      this.previousButton.disabled =
        this.isAtBeginning();

      this.nextButton.disabled =
        this.isAtEnd();
    }

    updateDots() {
      if (
        !this.options.dots ||
        !this.dotButtons.length
      ) {
        return;
      }

      const currentPage =
        this.getCurrentPage();

      this.dotButtons.forEach(
        (dot, index) => {
          const active = index === currentPage;

          dot.classList.toggle(
            "global-carousel__dot--active",
            active
          );

          dot.setAttribute(
            "aria-selected",
            active ? "true" : "false"
          );

          if (active) {
            dot.setAttribute(
              "aria-current",
              "true"
            );
          } else {
            dot.removeAttribute(
              "aria-current"
            );
          }
        }
      );
    }

    startAutoplay() {
      if (
        !this.options.autoplay ||
        this.reducedMotion ||
        document.hidden ||
        this.cards.length < 2 ||
        !this.hasOverflow() ||
        this.autoplayTimer
      ) {
        return;
      }

      this.autoplayTimer =
        window.setInterval(() => {
          if (!document.body.contains(this.track)) {
            this.stopAutoplay();
            return;
          }

          this.next();
        }, this.options.interval);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;

      window.clearInterval(
        this.autoplayTimer
      );

      this.autoplayTimer = null;
    }

    restartAutoplay() {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  function initializeCarousels(root = document) {
    addGlobalStyles();

    if (root.matches?.(selector)) {
      new GlobalCarousel(root);
    }

    root
      .querySelectorAll?.(selector)
      .forEach(track => {
        new GlobalCarousel(track);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initializeCarousels();
      }
    );
  } else {
    initializeCarousels();
  }

  document.addEventListener(
    "shopify:section:load",
    event => {
      initializeCarousels(event.target);
    }
  );

  window.initializeCarousels =
    initializeCarousels;
})();